/**
 * Shared SVG icons for loop player components
 * 
 * All icons use fill="currentColor" to inherit text color.
 * Based on Material Design and Lucide icon sets.
 * 
 * Pattern: svg`` for SVG fragments (paths), html`` for full <svg> elements.
 */

import { html, TemplateResult } from 'lit';

// ─── Control Bar Icons ─────────────────────────────────────────────────────

/** Previous track icon - bar + single triangle pointing left (for track navigation) */
export const previousIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z"/>
  </svg>
`;

/** Next track icon - single triangle + bar pointing right (for track navigation) */
export const nextIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M16 18h2V6h-2v12zM6 6v12l8.5-6L6 6z"/>
  </svg>
`;

/** Seek backward icon (rewind 5s) - double triangle pointing left */
export const seekBackwardIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z"/>
  </svg>
`;

/** Seek forward icon (fast forward 5s) - double triangle pointing right */
export const seekForwardIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z"/>
  </svg>
`;

/** Play icon */
export const playIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M8 5v14l11-7L8 5z"/>
  </svg>
`;

/** Pause icon */
export const pauseIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
  </svg>
`;

/** Repeat/Loop icon */
export const repeatIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
  </svg>
`;

/** Plus/Add icon */
export const plusIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
`;

/** Countdown/Hourglass icon */
export const countdownIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M6 2v6h.01L10 12l-3.99 4H6v6h12v-6h-.01L14 12l3.99-4H18V2H6zm10 18H8v-3.5l4-4 4 4V20zm0-11V4H8v3.5l4 4 4-4z"/>
  </svg>
`;

// ─── Edit Panel Icons ──────────────────────────────────────────────────────

/** Edit/Pencil icon */
export const editIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
`;

/** Timer/Clock icon (set to current time) */
export const timerIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 0 0 12 4c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
  </svg>
`;

/** Trash/Delete icon */
export const trashIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
`;

/** Check/Done icon */
export const checkIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
  </svg>
`;

/** Keyboard icon (for shortcuts) */
export const keyboardIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/>
  </svg>
`;

// ─── Handle Icons ──────────────────────────────────────────────────────────

/** Chevron down icon (Material Design) - rotates 180deg when expanded */
export const chevronDownIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
  </svg>
`;

/** Chevron Left icon (for drag handles) */
export const chevronLeftIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z"/>
  </svg>
`;

/** Chevron Right icon (for drag handles) */
export const chevronRightIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12l-4.58 4.59z"/>
  </svg>
`;

/** Zoom/Focus icon (magnifying glass) */
export const zoomIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
`;

/** List/Menu icon (for loop selector) */
export const listIcon: TemplateResult = html`
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
  </svg>
`;
